import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { ACTION_META, fmtDateTime } from './activityMeta';
import ExcelTable from '../../components/common/ExcelTable';

const C = { billed: '#6366f1', collected: '#10b981', out: '#ef4444', amber: '#f59e0b', sky: '#0ea5e9', slate: '#64748b' };
const STATUS_COLORS = { paid: '#10b981', partial: '#0ea5e9', pending: '#f59e0b', payment_requested: '#8b5cf6' };
const money = (n) => `SAR ${Math.round(n || 0).toLocaleString('en-US')}`;
const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fullDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
const sar2 = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

// Table view (rows/columns like Excel) is the default; graphs stay one click away.
const readView = () => { try { return localStorage.getItem('sa_dash_view') || 'table'; } catch { return 'table'; } };

const Kpi = ({ label, value, sub, color, onClick }) => (
  <div className="card" style={{ flex: '1 1 170px', minWidth: 170, cursor: onClick ? 'pointer' : 'default', borderTop: `3px solid ${color || C.billed}` }} onClick={onClick}>
    <div className="card-body" style={{ padding: 16 }}>
      <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

const Panel = ({ title, right, children, style }) => (
  <div className="card" style={{ marginBottom: 16, ...style }}>
    <div className="card-header"><strong>{title}</strong>{right}</div>
    <div className="card-body" style={{ paddingTop: 8 }}>{children}</div>
  </div>
);

const tip = { contentStyle: { borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(15,23,42,.12)', fontSize: 12 } };

// Saudi calendar day as YYYY-MM-DD (shops run on Saudi time, whatever the viewer's clock says)
const saudiDay = (offsetDays = 0) => new Date(Date.now() + offsetDays * 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });

// First day of the current Saudi month as YYYY-MM-DD
const saudiMonthStart = () => `${saudiDay(0).slice(0, 8)}01`;

// "Aaj ka dhandha" — sale (Quick Sale + orders) and money received for a Saudi date range, per shop.
const DayPanel = ({ shopId, from, to }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    superAdminApi.get('/insights/day', { params: { shopId: shopId || undefined, from, to } })
      .then(r => setD(r.data)).catch(() => toast.error('Failed to load sales total'));
  }, [shopId, from, to]);
  const t = d && d.totals;
  const openSales = (sid, type) => navigate(`/superadmin/sales?from=${from}&to=${to}${sid ? `&shopId=${sid}` : ''}${type ? `&saleType=${type}` : ''}`);
  const rangeLabel = from === to ? fullDate(from) : `${fullDate(from)} – ${fullDate(to)}`;
  return (
    <Panel
      title={<>Sales total (Saudi time){d && <span style={{ fontWeight: 400, color: '#64748b', fontSize: 13 }}> · {rangeLabel}{to === d.today && <> · now {d.saudiNow} in Saudi</>}</span>}</>}
      style={{ borderTop: `3px solid ${C.billed}` }}
    >
      {!t ? <div style={{ color: '#94a3b8' }}>Loading…</div> : (<>
        {/* Headline: all money that came in — the number the owner matches against the till + card machine */}
        <div onClick={() => navigate(`/superadmin/payments?from=${from}&to=${to}${shopId ? `&shopId=${shopId}` : ''}`)}
          style={{ cursor: 'pointer', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: '16px 20px', marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: '#047857', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {from === to && to === d.today ? "Today's total business" : 'Total business'} — all money received
          </div>
          <div style={{ fontSize: 40, fontWeight: 800, color: '#065f46', lineHeight: 1.15, marginTop: 4 }}>{money(t.collected)}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {Object.entries(d.byMethod).filter(([, v]) => Math.abs(v) > 0.001).sort((a, b) => b[1] - a[1]).map(([m, v]) => (
              <span key={m} style={{ background: '#fff', border: '1px solid #a7f3d0', borderRadius: 999, padding: '4px 12px', fontSize: 15 }}>
                {m} <strong>{money(v)}</strong>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 13, color: '#047857', marginTop: 10 }}>
            = {money(t.collectedNew)} from sales made in this period + {money(t.collectedOld)} balance paid on older orders · {t.entries} payments · click to see each one
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <Kpi label="New sales (bill value)" value={money(t.saleTotal)} sub={`${t.quickCount + t.orderCount} bills made · click for list`} color={C.billed} onClick={() => openSales(shopId)} />
          <Kpi label="Quick Sale" value={money(t.quickTotal)} sub={`${t.quickCount} counter sales`} color={C.sky} onClick={() => openSales(shopId, 'quick')} />
          <Kpi label="Stitching orders" value={money(t.orderTotal)} sub={`${t.orderCount} orders`} color={C.amber} onClick={() => openSales(shopId, 'order')} />
          <Kpi label="Money received" value={money(t.collected)}
            sub={Object.entries(d.byMethod).filter(([, v]) => Math.abs(v) > 0.001).map(([m, v]) => `${m} ${sar2(v)}`).join(' · ') || 'nothing received'}
            color={C.collected} onClick={() => navigate(`/superadmin/payments?from=${from}&to=${to}${shopId ? `&shopId=${shopId}` : ''}`)} />
        </div>
        {d.byShop.length > 0 && (
          <div className="table-container">
            <table>
              <thead><tr><th>Shop</th><th>Quick Sale</th><th>Orders</th><th>Total sale</th><th>Money received</th></tr></thead>
              <tbody>
                {d.byShop.map(r => (
                  <tr key={r.shopId} style={{ cursor: 'pointer' }} onClick={() => openSales(r.shopId)}>
                    <td><strong>{r.name}</strong></td>
                    <td>{money(r.quickTotal)} <span style={{ color: '#94a3b8' }}>({r.quickCount})</span></td>
                    <td>{money(r.orderTotal)} <span style={{ color: '#94a3b8' }}>({r.orderCount})</span></td>
                    <td><strong>{money(r.saleTotal)}</strong></td>
                    <td style={{ color: C.collected }}>{money(r.collected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d.byShop.length === 0 && <div style={{ color: '#94a3b8' }}>No sales in this period.</div>}
      </>)}
    </Panel>
  );
};

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  // One Saudi date range drives every section; opens on Today
  const [from, setFrom] = useState(saudiDay(0));
  const [to, setTo] = useState(saudiDay(0));
  const setRange = (f, t) => { setFrom(f); setTo(t); };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setViewState] = useState(readView);
  const setView = (v) => { setViewState(v); try { localStorage.setItem('sa_dash_view', v); } catch { /* ignore */ } };

  useEffect(() => { superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superAdminApi.get('/insights/analytics', { params: { shopId: shopId || undefined, from, to } });
      setData(r.data);
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, [shopId, from, to]);
  useEffect(() => { load(); }, [load]);

  const presets = [
    ['Today', saudiDay(0), saudiDay(0)],
    ['Yesterday', saudiDay(-1), saudiDay(-1)],
    ['7 days', saudiDay(-6), saudiDay(0)],
    ['30 days', saudiDay(-29), saudiDay(0)],
    ['This month', saudiMonthStart(), saudiDay(0)],
    ['90 days', saudiDay(-89), saudiDay(0)],
    ['1 year', saudiDay(-364), saudiDay(0)],
  ];

  if (loading && !data) return <div className="loading"><div className="spinner"></div></div>;
  if (!data) return null;
  const { kpis, series, statusBreakdown, byShop, byStaff, topCustomers, topDebtors, recentActivity } = data;
  const collectionRate = kpis.billed ? Math.round((kpis.collected / kpis.billed) * 100) : 0;
  const chartSeries = series.map(s => ({ ...s, label: shortDate(s.date) }));
  const periodLabel = from === to ? fullDate(from) : `${fullDate(from)} – ${fullDate(to)}`;

  return (
    <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <select className="form-control" style={{ width: 220 }} value={shopId} onChange={e => setShopId(e.target.value)}>
          <option value="">All Shops</option>
          {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {presets.map(([label, f, t]) => (
            <button key={label} className={`btn btn-sm ${from === f && to === t ? 'btn-primary' : 'btn-outline'}`} onClick={() => setRange(f, t)}>{label}</button>
          ))}
          <input type="date" className="form-control" style={{ width: 150 }} value={from} max={saudiDay(0)} onChange={e => e.target.value && setFrom(e.target.value)} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" className="form-control" style={{ width: 150 }} value={to} max={saudiDay(0)} onChange={e => e.target.value && setTo(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <button className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView('table')}>▦ Table</button>
          <button className={`btn btn-sm ${view === 'graph' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView('graph')}>📈 Graph</button>
        </div>
      </div>

      <DayPanel shopId={shopId} from={from} to={to} />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Kpi label="Total billed" value={money(kpis.billed)} sub={`${kpis.bills} bills · avg ${money(kpis.avgBill)}`} color={C.billed} />
        <Kpi label="Collected" value={money(kpis.collected)} sub="all money received in this period" color={C.collected} />
        <Kpi label="Outstanding (credit)" value={money(kpis.outstanding)} sub={`still owed on these bills · ${collectionRate}% collected`} color={C.out} onClick={() => navigate('/superadmin/customers')} />
        <Kpi label="New customers" value={kpis.customers} sub={`${kpis.totalCustomers} customers in total`} color={C.sky} onClick={() => navigate('/superadmin/customers')} />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Kpi label="Due for delivery" value={kpis.dueInRange} sub="delivery date in this period, not delivered" color="#f97316" />
        <Kpi label="Delivered" value={kpis.delivered} sub="handed over in this period" color={C.collected} />
        <Kpi label="Pending delivery" value={kpis.pendingDelivery} sub="not yet delivered, right now" color={C.amber} />
        <Kpi label="Overdue" value={kpis.overdue} sub="past delivery date, right now" color={C.out} />
      </div>

      {view === 'graph' ? (<>
      <Panel title={`Sales vs money collected — ${periodLabel}`}>
        <div style={{ height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={chartSeries} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gBilled" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.billed} stopOpacity={0.35} /><stop offset="95%" stopColor={C.billed} stopOpacity={0} /></linearGradient>
                <linearGradient id="gCol" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.collected} stopOpacity={0.35} /><stop offset="95%" stopColor={C.collected} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={{ fontSize: 11 }} width={48} />
              <Tooltip {...tip} formatter={(v) => money(v)} />
              <Legend />
              <Area type="monotone" dataKey="billed" name="Billed" stroke={C.billed} strokeWidth={2.5} fill="url(#gBilled)" />
              <Area type="monotone" dataKey="collected" name="Collected" stroke={C.collected} strokeWidth={2.5} fill="url(#gCol)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
        <Panel title="Bills per day" style={{ marginBottom: 0 }}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={chartSeries} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...tip} />
                <Bar dataKey="bills" name="Bills" fill={C.billed} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Payment status of bills" style={{ marginBottom: 0 }}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusBreakdown} dataKey="count" nameKey="status" innerRadius={55} outerRadius={90} paddingAngle={3}
                  label={({ count }) => count}>
                  {statusBreakdown.map(s => <Cell key={s.status} fill={STATUS_COLORS[s.status] || C.slate} />)}
                </Pie>
                <Tooltip {...tip} formatter={(v, n, p) => [`${v} bills · ${money(p.payload.amount)}`, p.payload.status]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Revenue by shop" style={{ marginBottom: 0 }}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={byShop} layout="vertical" margin={{ top: 6, right: 16, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip {...tip} formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="collected" name="Collected" stackId="a" fill={C.collected} />
                <Bar dataKey="outstanding" name="Outstanding" stackId="a" fill={C.out} radius={[0, 5, 5, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Staff performance (bills created)" style={{ marginBottom: 0 }}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={byStaff} layout="vertical" margin={{ top: 6, right: 16, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip {...tip} formatter={(v, n, p) => (n === 'Bills' ? v : money(v))} />
                <Bar dataKey="bills" name="Bills" fill={C.sky} radius={[0, 5, 5, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div style={{ height: 16 }} />
      <Panel title="Staff logins per day">
        <div style={{ height: 200 }}>
          <ResponsiveContainer>
            <LineChart data={chartSeries} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip {...tip} />
              <Legend />
              <Line type="monotone" dataKey="logins" name="Logins" stroke={C.collected} strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="failedLogins" name="Failed attempts" stroke={C.out} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      </>) : (<>
      <ExcelTable
        title={`Day-wise report — ${periodLabel} (newest first)`}
        fileName={`day-wise-report-${from}-to-${to}`}
        maxHeight={520}
        rows={[...series].reverse().map(r => ({ ...r, _key: r.date, credit: (r.billed || 0) - (r.collected || 0) }))}
        columns={[
          { key: 'date', label: 'Date', format: (v) => <strong>{fullDate(v)}</strong>, csv: (v) => v },
          { key: 'bills', label: 'Bills', align: 'right', total: true },
          { key: 'billed', label: 'Billed (SAR)', align: 'right', total: true, format: (v) => sar2(v), csv: (v) => Math.round((v || 0) * 100) / 100 },
          { key: 'collected', label: 'Collected (SAR)', align: 'right', total: true, format: (v) => <span style={{ color: '#047857', fontWeight: 600 }}>{sar2(v)}</span>, csv: (v) => Math.round((v || 0) * 100) / 100 },
          { key: 'credit', label: 'Billed − Collected (SAR)', align: 'right', total: true, format: (v) => <span style={{ color: v > 0 ? '#b91c1c' : '#047857' }}>{sar2(v)}</span>, csv: (v) => Math.round((v || 0) * 100) / 100 },
          { key: 'logins', label: 'Staff logins', align: 'right', total: true },
          { key: 'failedLogins', label: 'Failed logins', align: 'right', total: true, format: (v) => (v ? <span style={{ color: '#b91c1c' }}>{v}</span> : 0) },
        ]}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16 }}>
        <ExcelTable
          title="Shop-wise"
          fileName="shop-wise"
          rows={byShop.map(r => ({ ...r, _key: r.shopId }))}
          columns={[
            { key: 'name', label: 'Shop', format: (v) => <strong>{v}</strong> },
            { key: 'bills', label: 'Bills', align: 'right', total: true },
            { key: 'billed', label: 'Billed (SAR)', align: 'right', total: true, format: (v) => sar2(v) },
            { key: 'collected', label: 'Collected (SAR)', align: 'right', total: true, format: (v) => sar2(v) },
            { key: 'outstanding', label: 'Outstanding (SAR)', align: 'right', total: true, format: (v) => <span style={{ color: '#b91c1c' }}>{sar2(v)}</span> },
          ]}
        />
        <ExcelTable
          title="Staff-wise (bills created)"
          fileName="staff-wise"
          rows={byStaff.map(r => ({ ...r, _key: r.userId }))}
          columns={[
            { key: 'name', label: 'Staff', format: (v) => <strong>{v}</strong> },
            { key: 'shop', label: 'Shop' },
            { key: 'bills', label: 'Bills', align: 'right', total: true },
            { key: 'billed', label: 'Billed (SAR)', align: 'right', total: true, format: (v) => sar2(v) },
          ]}
        />
        <ExcelTable
          title="Payment status of bills"
          fileName="payment-status"
          rows={statusBreakdown.map(r => ({ ...r, _key: r.status }))}
          columns={[
            { key: 'status', label: 'Status', format: (v) => <span style={{ color: STATUS_COLORS[v] || C.slate, fontWeight: 700, textTransform: 'capitalize' }}>{String(v).replace('_', ' ')}</span> },
            { key: 'count', label: 'Bills', align: 'right', total: true },
            { key: 'amount', label: 'Amount (SAR)', align: 'right', total: true, format: (v) => sar2(v) },
          ]}
        />
      </div>
      </>)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Panel title="Top customers (by billed)" style={{ marginBottom: 0 }}>
          {topCustomers.map(c => (
            <div key={c.customerId} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
              onClick={() => navigate(`/superadmin/customers/${c.customerId}`)}>
              <span style={{ fontWeight: 600, color: '#3b82f6' }}>{c.name}</span>
              <span style={{ fontSize: 13 }}>{c.bills} bills · <strong>{money(c.billed)}</strong></span>
            </div>
          ))}
          {topCustomers.length === 0 && <div style={{ color: '#94a3b8' }}>No data</div>}
        </Panel>
        <Panel title="Customers with highest credit (owed)" style={{ marginBottom: 0 }}>
          {topDebtors.map(c => (
            <div key={c.customerId} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
              onClick={() => navigate(`/superadmin/customers/${c.customerId}`)}>
              <span style={{ fontWeight: 600, color: '#3b82f6' }}>{c.name}</span>
              <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 700 }}>{money(c.remaining)}</span>
            </div>
          ))}
          {topDebtors.length === 0 && <div style={{ color: '#94a3b8' }}>Nobody owes anything 🎉</div>}
        </Panel>
      </div>

      <Panel title="Live activity" right={<button className="btn btn-outline btn-sm" onClick={() => navigate('/superadmin/activity')}>Full history →</button>}>
        {recentActivity.length === 0 && <div style={{ color: '#94a3b8' }}>Activity tracking started — new actions will appear here.</div>}
        {recentActivity.map(a => {
          const m = ACTION_META[a.action] || { label: a.action, color: '#64748b' };
          return (
            <div key={a._id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
              <span style={{ background: `${m.color}1a`, color: m.color, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{m.label}</span>
              <div style={{ flex: 1, fontSize: 13 }}>
                {a.summary}
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDateTime(a.at)} · {a.userName || 'system'}{a.userRole ? ` (${a.userRole})` : ''}{a.shop ? ` · ${a.shop}` : ''}</div>
              </div>
            </div>
          );
        })}
      </Panel>
    </div>
  );
}
